import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LOGIN_ROUTE, inlineRenderFor, t, type LinkPermission } from '@bms2/shared';
import { Button, TextInput } from '../components/controls';
import { FileViewer } from '../components/composites/FileViewer';
import { useFetchedBlob } from '../components/files/useFetchedBlob';
import { Size } from '@bms2/shared';
import { Text } from '../components/Text';
import { PublicBand } from '../shell/PublicBand';
import { apiFetch } from './records/api';
import { readSessionToken } from '../session';
import styles from './sharedFile.module.css';

/**
 * Where a share link lands (piece 9; decision 0361). 🔨 The page stands outside
 * the shell, the way the Login Screen does: the recipient is somebody the
 * building sent a link to, and the frame is the signed-in application.
 *
 * 🔨 It grants nothing. The server decides on every request, and this page only
 * says which answer came back: sign in, the link is spent, or here is the file.
 * 💰 The Share flow, the two guards and the File Viewer were all built and the
 * address the mail carried had no page behind it at all, so every recipient met
 * the shell's Not found (found while building, 2026-09-13).
 *
 * 🔨 The viewer fetches the bytes and draws them itself, never a plug-in element
 * pointed at the route (decision 0549). The deployed policy carries
 * `object-src 'none'`, so the `object` tag this page was built with never issued
 * a request at all and every recipient met the fallback text instead of the
 * file. 💰 A signed-in reader opening their own share link met it too, which is
 * how the product owner found it (found while building, 2026-09-18).
 */
export const SHARE_LANDING_PATH = '/files/share';

type State =
  | { kind: 'reading' }
  | { kind: 'needsSession' }
  | { kind: 'refused'; message: string }
  | { kind: 'ready'; fileName: string; permission: LinkPermission };

export function SharedFilePage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { linkId = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'reading' });
  const [askEmail, setAskEmail] = useState('');
  const [asked, setAsked] = useState(false);
  const streamUrl = `${apiBaseUrl}${SHARE_LANDING_PATH}/${linkId}/stream`;
  const opened = state.kind === 'ready';
  /*
   * 🔨 The bytes come through the one fetch-and-blob path every file the
   * application draws uses, because the page cannot hand its session to a tag
   * (decision 0549): an `object` or an `embed` carries the cookie but the
   * deployed policy refuses to load either one, and a tag that never loads has
   * no failure the page can report. Nothing is asked for until the facts route
   * says the link opens.
   * 🔨 The render is chosen from the served `Content-Type` alone, which the
   * proxy takes from the stored mimeType and never sniffs (piece 9). A type that
   * draws in neither element, and a stream that refused, are one outcome here:
   * the reader is told, not left waiting.
   */
  const bytes = useFetchedBlob(opened ? streamUrl : null);
  const render = bytes.kind === 'drawn' ? inlineRenderFor(bytes.contentType) : null;
  const undrawable = bytes.kind === 'failed' || (bytes.kind === 'drawn' && render === null);

  useEffect(() => {
    if (!linkId) return undefined;
    const controller = new AbortController();
    /*
     * 🔨 `apiFetch`, not a bare `fetch`: it carries the session the way every
     * other read does, and it already holds the rule for a request that never
     * had one — the Login Screen, then back to the page that was asked for.
     * 💰 Written with a bare fetch first, which carried no session at all, so
     * the page told a signed-in reader the file could not be reached (found
     * while building, 2026-09-13). The panel below is the other case: a token
     * that is held and refused, where `apiFetch` clears it and stays put.
     */
    // 🔒 T385 (P9-2): this read opts out of the frame's redirect-to-Login on 401 (decision 1165). It is the one
    // page built for a reader who has no login yet, and the panel below is that reader's own way out.
    apiFetch(`${apiBaseUrl}${SHARE_LANDING_PATH}/${linkId}`, { signal: controller.signal }, { skipAuthRedirect: true })
      .then(async (response) => {
        if (response.status === 401) return setState({ kind: 'needsSession' });
        const body = (await response.json().catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null)) as { fileName?: string; permission?: LinkPermission; message?: string } | null;
        if (!response.ok) return setState({ kind: 'refused', message: body?.message ?? t('share.landing.refused') });
        return setState({ kind: 'ready', fileName: body?.fileName ?? '', permission: body?.permission ?? 'View' });
      })
      .catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => {
        if (!controller.signal.aborted) setState({ kind: 'refused', message: t('share.landing.unreachable') });
      });
    return () => controller.abort();
  }, [apiBaseUrl, linkId]);

  /*
   * 🔨 Both dead ends offer the same way out (decision 0365). A reader with no
   * login and a reader whose link has lapsed are in the same position: they
   * hold an address they cannot open and no way to say so. Asking creates a
   * to-do for whoever sent it, and grants nothing.
   */
  const ask = (
    <div className={styles.ask} data-share-landing="ask">
      {asked ? (
        <Text variant="body">{t('share.landing.asked')}</Text>
      ) : (
        <>
          <Text variant="caption">{t('share.landing.askLead')}</Text>
          <TextInput size={Size.Medium} label={t('share.landing.askEmail')} value={askEmail} onChange={setAskEmail} type="email" />
          <Button
            size={Size.Small}
            text={[t('share.landing.askAction')]}
            disabled={askEmail.trim() === ''}
            actions={[() => {
              // 🔨 The answer is the same whatever the server found, so nothing here reads it.
              void apiFetch(`${apiBaseUrl}${SHARE_LANDING_PATH}/${linkId}/request-access`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ email: askEmail.trim() }),
              }).catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => undefined);
              setAsked(true);
            }]}
          />
        </>
      )}
    </div>
  );

  /*
   * 🔒 The way in, drawn as the page's one filled control rather than a line of
   * link text (T216, 2026-09-24): the owner, holding no session, read the
   * panel as having no login on it. A whole-document navigation, because the
   * Login Screen is where the session is made and this page holds none.
   */
  const signIn = (
    <a className={styles.signIn} href={`${LOGIN_ROUTE}?next=${encodeURIComponent(`${SHARE_LANDING_PATH}/${linkId}`)}`} data-share-landing="sign-in">{t('share.landing.signInAction')}</a>
  );

  return (
    <main className={styles.page} data-page="shared-file">
      {/* 🔒 The mark on the shell's own dark band, never on the page's light ground, where it vanished (decision 0690). */}
      <PublicBand />
      <div className={styles.body}>
        {state.kind === 'reading' ? <Text variant="small">{t('share.landing.reading')}</Text> : null}
        {state.kind === 'needsSession' ? (
          <div className={styles.panel} data-share-landing="needs-session">
            {/* 🔒 Every link asks for a login (decision 0191), so the sentence names that rule and nothing about the file: the page holds no session and cannot say whether the file is under an agreement (T215). */}
            <Text variant="body">{t('share.landing.signIn')}</Text>
            {signIn}
            {ask}
          </div>
        ) : null}
        {/* 🔨 Not an Alert Banner: a banner that states a problem offers an action (piece 3),
            and a spent or refused link offers none — there is nothing the reader can do here
            but ask the sender again. The refusal is the panel's own text. */}
        {state.kind === 'refused' ? (
          <div className={styles.panel} data-share-landing="refused">
            <Text variant="body">{state.message}</Text>
            {/* 🔒 A refusal met with no session held is not the reader's final answer, so it offers the way in too (T216). */}
            {readSessionToken() === null ? (
              <>
                <Text variant="caption">{t('share.landing.refusedSignIn')}</Text>
                {signIn}
              </>
            ) : null}
            <Text variant="caption">{t('share.landing.askSender')}</Text>
            {ask}
          </div>
        ) : null}
        {state.kind === 'ready' ? (
          <FileViewer
            permission={state.permission}
            fileName={state.fileName}
            onDownload={() => window.open(`${streamUrl}?download=1`, '_blank', 'noopener')}
            onPrint={() => window.print()}
          >
            {bytes.kind === 'fetching' ? <Text variant="small">{t('share.landing.reading')}</Text> : null}
            {undrawable ? <Text variant="small">{t('share.landing.cannotDraw')}</Text> : null}
            {bytes.kind === 'drawn' && render === 'frame' ? (
              /*
               * 🔨 A View-only link draws the frame with the reader's own PDF
               * toolbar suppressed (decision 0549). The browser's toolbar carries
               * its own Download and Print, which is exactly the careless
               * disclosure the viewer's own toolbar withholds on this permission
               * (decision 0072). It stops the careless reader, not a determined
               * one, which is the whole of what these controls claim.
               */
              <iframe
                className={styles.document}
                src={state.permission === 'View' ? `${bytes.url}#toolbar=0` : bytes.url}
                title={state.fileName}
                data-share-document="frame"
              />
            ) : null}
            {bytes.kind === 'drawn' && render === 'image' ? (
              <img className={styles.document} src={bytes.url} alt={state.fileName} data-share-document="image" />
            ) : null}
          </FileViewer>
        ) : null}
      </div>
    </main>
  );
}
